import axios, { AxiosError, isAxiosError } from 'axios';

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 0,
    },
    mutations: {
      throwOnError: (error) => !isAxiosError(error),
    },
  },
});

const http = axios.create();

export const addResponseErrorInterceptor = (logout: () => any) => {
  http.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && error.response.status === 401) {
        logout();
      } else {
        throw error;
      }
      return Promise.reject(error);
    }
  );
};

export const addAccessTokenInterceptor = (
  getAccessTokenSilently: () => Promise<string>,
  onTokenRefresh: (token: string) => void
) => {
  http.interceptors.request.use(
    async (config) => {
      let accessToken: string;
      try {
        accessToken = await getAccessTokenSilently();
      } catch (error) {
        // Auth0 rejects with its own errors (login_required, Failed to fetch, timeouts). Rethrown as an
        // AxiosError so a mutation's throwOnError treats it like any failed request instead of crashing
        // the app to the ErrorBoundary. An expired session still redirects via withAuthenticationRequired.
        throw AxiosError.from(error, AxiosError.ERR_NETWORK, config);
      }
      config.headers.Authorization = `Bearer ${accessToken}`;
      onTokenRefresh(accessToken);
      return config;
    },
    (error) => Promise.reject(error)
  );
};

export const setBearerToken = (token: string) => {
  http.interceptors.request.use((request) => {
    request.headers.Authorization = `Bearer ${token}`;
    return request;
  });
};

export const addBaseUrl = (baseUrl: string) => {
  http.defaults.baseURL = baseUrl;
};

http.defaults.headers.common.Accept = 'application/json';
http.defaults.headers.common['Content-Type'] = 'application/json';

export default http;
